/* eslint-disable import/no-unresolved */
/* eslint-disable no-param-reassign */
import { useState, useEffect, useRef, useMemo } from "react";
import { callApi, callApiIO } from "@src/api";

import { useAuth } from "@src/hooks/useAuth";
import moment from "moment";
import { YMD_FORMAT, DAYS_OF_WEEK } from "@src/constants/date";
import {
  each,
  find,
  filter,
  flatten,
  map,
  includes,
  extend,
  sumBy,
  uniq,
  maxBy,
} from "lodash";
import { toast } from "react-toastify";
import UseEmployeeData from "./UseEmployeeData";

const dow = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const defaultCalendarSettings = {
  mode: "manual",
  snap: "00:15",
  slot: "00:30",
  viewDays: 14,
  syncDays: 28,
  defaultStartTime: "10:00",
  defaultDuration: "09:00",
  storeHours: {
    start: "07:00",
    end: "22:00",
  },
};

const useScheduleData = () => {
  const [fetchLoading, setFetchLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [calendarSettings, setCalendarSettings] = useState(
    defaultCalendarSettings
  );
  const firstRenderRef = useRef(false);
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [refetch, setRefetch] = useState(false);
  
  // Flag to skip addBatch after restore (to prevent deleting restored drafts)
  const skipAddBatchRef = useRef(false);
  
  // Function to set the skip flag (called before changing selectedDate after restore)
  const setSkipAddBatch = (value) => {
    skipAddBatchRef.current = value;
  };

  const [selectedTemplate, setSelectedTemplate] = useState(0);

  const [hasUnsavedDraft, setHasUnsavedDraft] = useState(false);

  const [mode, setMode] = useState("publish");

  const [modifiedBusinessIds, setModifiedBusinessIds] = useState(new Set());

  useEffect(() => {
    if (mode !== "template") {
      setModifiedBusinessIds(new Set());
    }
  }, [mode]);

  // const modePublished = true;
  const [nDays, setNDays] = useState(calendarSettings.viewDays);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [period, setPeriod] = useState([
    moment().format("YYYY-MM-DD"),
    moment()
      .add(calendarSettings.viewDays - 1, "days")
      .format("YYYY-MM-DD"),
  ]);

  const dMD = "ddd, MMM DD";
  // const hma = "hh:mm A";

  // const startDur = moment.duration(calendarSettings.storeHours.start);
  const endDur = moment.duration(calendarSettings.storeHours.end);

  // const dayStartHr = startDur.hours();
  // const dayStartMin = startDur.minutes();
  const dayEndHr = endDur.hours();
  const dayEndMin = endDur.minutes();

  const {
    selectedBusiness,
    updateCurrentBusiness,
    // business,
  } = useAuth();

  const id = selectedBusiness?.id;

  // getEmployeeData
  const { employeeData, getEmployeeData, updateEmployeeAppointmentVisibility, updateEmployeeData } =
    UseEmployeeData();

  const [publishedEvents, setPublishedEvents] = useState([]);

  const [selectedBusinessIds, setSelectedBusinessIds] = useState(
    id ? [id] : []
  );

  const [loadedBusinessIds, setLoadedBusinessIds] = useState(
    new Set(id ? [id] : [])
  );

  // Schedule data keyed by businessId
  const [schedulesByBusiness, setSchedulesByBusiness] = useState({});
  const [periodScheduleByBusiness, setPeriodScheduleByBusiness] = useState({});

  const allData = useMemo(
    () => selectedBusinessIds.flatMap(bid => schedulesByBusiness[bid] ?? []),
    [selectedBusinessIds, schedulesByBusiness]
  );

  const allTemplateData = useMemo(
    () => flatten(map(allData, "staticschedules")),
    [allData]
  );

  const periodicSchedule = useMemo(
    () => selectedBusinessIds.flatMap(bid => periodScheduleByBusiness[bid] ?? []),
    [selectedBusinessIds, periodScheduleByBusiness]
  );

  const [holidaysByBusiness, setHolidaysByBusiness] = useState({});

  useEffect(() => {
    setSelectedBusinessIds(id ? [id] : []);
    setLoadedBusinessIds(new Set(id ? [id] : []));
    setSchedulesByBusiness({});
    setPeriodScheduleByBusiness({});
    setHolidaysByBusiness({});
  }, [id]);

  const locationsLoaded = selectedBusinessIds.length > 1;

  const getPastSchedule = async ({ start, end, businessIds = selectedBusinessIds }) => {
    setFetchLoading(true);
    const isMulti = businessIds.length > 1;
    const effectiveId = businessIds[0] ?? id;
    const isNonPrimary = effectiveId !== id;

    const res = await callApi(
      `/protected/business/${id}/staff/schedules`,
      "GET",
      null,
      null,
      {
        start,
        end,
        ...(isMulti && { network: true, businessIds }),
        ...(!isMulti && isNonPrimary && { network: true, businessIds: [effectiveId] }),
      }
    );
    if (res?.status) {
      const filtered = res.data?.flatMap(x => x).filter(x => !x.archived_at);
      if (isMulti) {
        const grouped = {};
        businessIds.forEach(bid => { grouped[bid] = []; });
        filtered.forEach(s => {
          const bid = s.business_id;
          if (!grouped[bid]) grouped[bid] = [];
          grouped[bid].push(s);
        });
        setPeriodScheduleByBusiness(prev => ({ ...prev, ...grouped }));
      } else {
        setPeriodScheduleByBusiness(prev => ({
          ...prev,
          [effectiveId]: filtered,
        }));
      }
    }
    setFetchLoading(false);
    return res;
  };

  // const past =
  //   moment(selectedDate).format("YYYY-MM-DD") < moment().format("YYYY-MM-DD");

  // to get the dynamic schedule data
  const getDynamicSchedules = async (startDate = null, businessIds = selectedBusinessIds) => {
    setFetchLoading(true);
    const effectiveDate = startDate || selectedDate;
    const params = {
      start: moment(effectiveDate).format(YMD_FORMAT),
      end: moment(effectiveDate)
        .add(calendarSettings.viewDays - 1, "days")
        .format(YMD_FORMAT),
    };

    const isMulti = businessIds.length > 1;
    const effectiveId = businessIds[0] ?? id;
    const isNonPrimary = effectiveId !== id;

    const response = await callApi(
      `/protected/business/${id}/schedule/get/dynamic`,
      "get",
      null,
      null,
      { 
        start: params.start,
        ...(isMulti && { network: true, end: params.end, businessIds }),
        ...(!isMulti && isNonPrimary && { network: true, end: params.end, businessIds: [effectiveId] }),
      } 
    );
    setFetchLoading(false);

    if (response?.status) {
      if (isMulti) {
        // Group by businessId and merge into existing state
        const grouped = {};
        businessIds.forEach(bid => { grouped[bid] = []; }); 
        response.data.forEach(emp => {
          const bid = emp.business_id;
          if (!grouped[bid]) grouped[bid] = [];
          grouped[bid].push(emp);
        });
        setSchedulesByBusiness(prev => ({ ...prev, ...grouped }));
      } else {
        setSchedulesByBusiness(prev => ({
          ...prev,
          [effectiveId]: response.data,
        }));
      }
    }
    return response;
  };

  // Simple refresh function that just fetches data without deleting drafts
  // Used after restoring archived schedules
  const refreshSchedules = async (startDate = null) => {
    await getDynamicSchedules(startDate);
  };

  const scopedEmployeeData = useMemo(
    () => employeeData?.filter(
      emp => selectedBusinessIds.includes(emp.business_id)
    ) ?? [],
    [employeeData, selectedBusinessIds]
  );

  const getStaffList = () => {
    const staff = scopedEmployeeData;
    const defaultSchedules = [];
    each(staff, (emp) => {
      if (emp.status === "ACTIVE") {
        defaultSchedules.push({
          active: false,
          visible: true,
          start: calendarSettings.defaultStartTime,
          duration: calendarSettings.defaultDuration,
          employeeId: emp.id,
          title: emp.name || emp.nickname || emp.full_name,
          color: emp.attributes && emp.attributes.schedulerColor,
          attributes: emp.attributes,
          temp: true,
          stick: true,
        });
      }
    });
    return defaultSchedules;
  };

  const defaultSchedules = getStaffList();

  const getResourceIdForEvent = (sched) =>
    `${sched.day || DAYS_OF_WEEK[sched.dow]}-${sched.employee_id}`;

  const getGroupTitleForEvent = (sched) =>
    `${
      (sched.day && moment(sched.day).format(dMD)) || DAYS_OF_WEEK[sched.dow]
    }`;

  const getInactive = (eid) => {
    const emp = find(employeeData, { id: eid });
    const isActive = emp && emp.status === "ACTIVE";
    return emp
      ? (emp.nickname || emp.full_name) + (isActive ? "" : "(INACTIVE)")
      : "Unknown Staff";
  };

  const getTitle = (staff, sched) =>
    staff ? staff.title : getInactive(sched.employee_id);

  const getEvent = (sched, isFuture) => {
    const offset =
      moment()
        .startOf("day")
        .add(1, "day")
        .diff(moment().startOf("day"), "hours") - 24;
    const day = moment().startOf("day").add(offset, "hours");
    const resId = sched.day || sched.dow;
    const start = moment(day).add(moment.duration(sched.start_time));
    const end = moment(day).add(moment.duration(sched.end_time)).isBefore(start)
      ? moment(day).add(dayEndHr, "hours").add(dayEndMin, "minutes")
      : day.add(moment.duration(sched.end_time));
    const staff = find(defaultSchedules, {
      employeeId: sched.employee_id,
    });

    return {
      id: String(sched.id),
      resourceId: getResourceIdForEvent(sched),
      groupTitle: getGroupTitleForEvent(sched),
      color:
        mode === "publish" && !isFuture
          ? "#666"
          : staff
          ? staff.color || "#03a9f4"
          : "#666",
      title: getTitle(staff, sched),
      start: start.toDate(),
      end:
        end <= start ? moment(start).add(5, "minutes").toDate() : end.toDate(),
      editable: mode !== "publish",
      overlap: false,
      extendedProps: {
        startMoment: start,
        endMoment: end,
        startHm: start.format("HH:mm"),
        endHm: end.format("HH:mm"),
        resId,
        published: sched.published,
        past: sched.past,
        dynamic: sched.day && !sched.dow,
        employeeId: sched.employee_id,
        conflicts: sched.conflicts,
        dow: sched.dow,
        day: sched.day,
        groupId: sched.groupId,
        detail: sched.detail,
      },
    };
  };

  const getEventSourceIdForEvent = (ev) => {
    const status =
      ev.published || (ev.extendedProps && ev.extendedProps.published)
        ? "published"
        : "draft";
    const staff =
      ev.employeeId || (ev.extendedProps && ev.extendedProps.employeeId) || "0";
    const evMode = ev.day ? "dynamic" : "dynamic";
    const dynMode = status === "published" ? evMode : "dynamic";
    return `${dynMode + status}staff${staff}`;
  };

  const createNewResource = (ev, Id) => ({
    id: Id,
    published: ev.extendedProps.published,
    past: ev.extendedProps.past,
    dynamic: ev.extendedProps.dynamic,
    employeeId: ev.extendedProps.employeeId,
    events: [],
  });

  // const isFutureDate = (x) =>
  //   x ? moment(x, YMD_FORMAT).startOf("day").isAfter(now) : false;

  const getEventsFromSchedule = (schedules) => {
    const now = moment().startOf("day").subtract(1, "minute");
    const dynamic = mode !== "template";
    const sources = {};
    const eventSources = [];
    const events = [];

    each(schedules, (sched) => {
      const isFuture = sched.day
        ? moment(sched.day, YMD_FORMAT).startOf("day").isAfter(now)
        : true;

      if (
        (dynamic &&
          sched.day &&
          !sched.dow &&
          (sched.published || !isFuture || (isFuture && mode === "edit"))) ||
        (!dynamic && sched.dow >= 0 && !sched.day)
      ) {
        events.push(getEvent(sched, isFuture));
      }
    });
    each(events, (ev) => {
      const eventID = getEventSourceIdForEvent(ev);
      if (!sources[eventID]) {
        sources[eventID] = createNewResource(ev, eventID);
      }
      sources[eventID].events.push(ev);
    });
    each(sources, (value) => {
      eventSources.push(value);
    });
    return eventSources;
  };

  const staticSchedules = filter(
    flatten(map(allData, "staticschedules")),
    (s) =>
      s &&
      s.employee_id &&
      s.published &&
      s.template_id === selectedTemplate &&
      !!find(defaultSchedules || [], {
        employeeId: s.employee_id,
      })
  );

  const dynamicSchedules = filter(
    flatten(map(allData, "dynamicschedules")),
    (s) =>
      s &&
      s.employee_id &&
      !!find(defaultSchedules || [], {
        employeeId: s.employee_id,
      })
  );

  const allSchedules = getEventsFromSchedule(
    mode !== "template"
      ? mode === "publish"
        ? periodicSchedule
        : dynamicSchedules
      : staticSchedules
  );

  const getEventSourceIdForStaff = (staff, pub) => {
    const status = pub ? "published" : "draft";
    const dynMode = status === "published" ? "dynamic" : "dynamic";
    return `${dynMode + status}staff${staff}`;
  };

  const defaultSchedulesWithHours = each(defaultSchedules, (staff) => {
    const pub = mode !== "edit";
    const sid = getEventSourceIdForStaff(staff.employeeId, pub);
    const sched = find(allSchedules, {
      id: sid,
    });
    const dur = sched
      ? sumBy(sched.events, (ev) =>
          moment(ev.end).diff(moment(ev.start), "minutes")
        )
      : 0;
    const hr = dur ? (dur / 60).toFixed(2) : 0;
    const days = sched ? uniq(sched.events, "resourceId") : [];
    const ds = "D/";
    const str = days.length ? `${days.length + ds + hr}Hrs` : "";
    staff.thrs = dur;
    staff.tdays = days.length;
    staff.totalHours = str;
  });

  const conflictingSchedules = map(
    filter(allSchedules, (item) => item.conflicts),
    (item) =>
      `${mode !== "publish" ? item.day : dow[item.dow]}-${item.employee_id}`
  );

  const getViewCalResources = () => {
    const calResources = [];
    // let conflictingSchedules = [];
    let i;
    let resObj;
    const day = mode === "publish" ? moment(period[0]) : moment(selectedDate);
    const nDaysTemp =
      mode === "publish"
        ? moment(period[1]).diff(moment(period[0]), "days") + 1
        : nDays;
    const activeStaff = filter(employeeData, (e) => e.status === "ACTIVE");
    const addCalResource = (resObjData) => {
      each(activeStaff, (e) => {
        const name = e.full_name + (e.nickname ? `\n${e.nickname}` : "");
        const eID = `${resObjData.groupId}-${e.id}`;
        const conflicts = includes(conflictingSchedules, eID);
        calResources.push(
          extend(
            {
              id: eID,
              conflicts,
              title: name,
              employeeId: e.id,
            },
            resObjData
          )
        );
      });
    };
    for (i = 0; i < nDaysTemp; i += 1) {
      resObj = {
        template: false,
        groupId: day.format(YMD_FORMAT),
        m: moment(day),
        groupTitle: day.format(dMD),
        dow: day.day(),
      };
      addCalResource(resObj);
      day.add(1, "day");
    }
    for (i = 0; i < 7; i += 1) {
      resObj = {
        template: true,
        day: day.format(YMD_FORMAT),
        groupId: DAYS_OF_WEEK[i],
        m: moment(day),
        groupTitle: DAYS_OF_WEEK[i],
        dow: i,
      };
      addCalResource(resObj);
    }

    return filter(calResources, {
      template: mode === "template",
    });
  };

  const isSourceVisible = (src) => {
    const res = find(
      defaultSchedules,
      (item) => item.employeeId === src.employeeId
    );
    if (res) {
      return res.visible;
    }
    return true;
  };

  const showPublished = () => {
    const staticSchedule = filter(
      flatten(map(allData, "staticschedules")),
      (s) =>
        s &&
        s.employee_id &&
        s.published &&
        !!find(defaultSchedules || [], {
          employeeId: s.employee_id,
        })
    );

    const dynamicSchedule = filter(
      flatten(map(allData, "dynamicschedules")),
      (s) =>
        s &&
        s.employee_id &&
        !!find(defaultSchedules || [], {
          employeeId: s.employee_id,
        })
    );

    if (dynamicSchedule?.filter((el) => !el.published)?.length)
      setDraftAvailable(true);
    const allSchedule = getEventsFromSchedule(
      mode !== "template"
        ? mode === "publish"
          ? periodicSchedule
          : dynamicSchedules
        : staticSchedule
    );
    const source = [];
    const resources = map(getViewCalResources(), "employeeId");

    const p = filter(
      allSchedule,
      (item) =>
        (mode === "edit" ? !item.published : item.published || item.past) &&
        !!find(
          item.events,
          (e) =>
            (mode === "edit"
              ? true
              : e.extendedProps.published || e.extendedProps.past) &&
            includes(resources, e.extendedProps.employeeId)
        )
    );

    each(p, (item) => {
      if (isSourceVisible(item)) {
        source.push(item);
      }
    });
    return source;
  };

  const getResources = () => {
    const viewCalResources = getViewCalResources();
    const visibleStaff = map(
      filter(defaultSchedules, (emp) => emp.visible),
      "employeeId"
    );

    // const emp = filter(viewCalResources |st| [], (res) => includes(visibleStaff, res.employeeId));
    return filter(viewCalResources || [], (res) =>
      includes(visibleStaff, res.employeeId)
    );
  };

  // const removeSource = (idOrArr) => {
  //     let sources = idOrArr ? (isArray(idOrArr) ? idOrArr : [idOrArr]) : null;
  //     if (viewcalendar) {
  //         viewcalendar.batchRendering(() => {
  //             if (!isEmpty(sources)) {
  //                 each(sources, (src) => {
  //                     const source = calendarMethod('getEventSourceById', src);
  //                     if (source) {
  //                         source.remove();
  //                     }
  //                 });
  //             }
  //             else {
  //                 sources = calendarMethod('getEventSources');
  //                 each(sources, (source) => {
  //                     source.remove();
  //                 });
  //             }
  //         });
  //     }
  //   }

  // const updateEventSources = () => {
  //   removeSource();
  //   if (editMode) {
  //       showDraft();
  //   } else {
  //       showPublished();
  //   }
  //   updateStaffHours();
  //   calendarMethod('refetchResources')
  // }

  // const Schedule = function (obj) {
  //   const schedule = obj || {};
  //     const st = moment(schedule.start);
  //     const en = moment(schedule.end);
  //     const startHour = st.hour() || 0;
  //     const startMin = st.minute() || 0;
  //     const endHour = en.hour() || 0;
  //     const endMin = en.minute() || 0;
  //     const r =
  //       (schedule && schedule.resource && schedule.resource.toJSON()) ||
  //       (schedule && schedule.getResources && schedule.getResources()[0]);
  //     const res =
  //       r ||
  //       find( getViewCalResources(), {
  //         employeeId: schedule.extendedProps.employeeId,
  //       });
  //     const nth = function () {};
  //     const that = this;
  //     const hasChanged = function () {
  //       let same = false;
  //         const resId =
  //           mode === "publish"
  //             ? schedule.extendedProps.resId
  //             : DAYS_OF_WEEK[schedule.extendedProps.resId];
  //       if (schedule && schedule.id) {
  //         same =
  //           schedule.extendedProps.startHm === that.startHm &&
  //           schedule.extendedProps.endHm === that.endHm &&
  //           resId === that.schedDay;
  //         return !same;
  //       }
  //       return true;
  //     };
  //     const update = function () {
  //       const stHr = that.start ? that.start.hour() : startHour;
  //         const stMin = that.start ? that.start.minute() : startMin;
  //         const endHr = that.end ? that.end.hour() : endHour;
  //         const endM = that.end ? that.end.minute() : endMin;
  //         const setStartHr = stHr < dayStartHr ? dayStartHr : stHr;
  //         const setStartMin =
  //           stHr < dayStartHr
  //             ? dayStartMin
  //             : stHr > dayStartHr
  //             ? stMin
  //             : stMin > dayStartMin
  //             ? stMin
  //             : dayStartMin;
  //         const setEndHr = endHr <= dayEndHr ? endHr : dayEndHr;
  //         const setEndMin =
  //           endHr > dayEndHr
  //             ? dayEndMin
  //             : endHr < dayEndHr
  //             ? endM
  //             : endM <= dayEndMin
  //             ? endM
  //             : dayEndMin;

  //       that.start = moment(res.extendedProps.m)
  //         .hour(setStartHr)
  //         .minute(setStartMin);
  //       that.end = moment(res.extendedProps.m).hour(setEndHr).minute(setEndMin);
  //       that.startHm = that.start.format("HH:mm");
  //       that.endHm = that.end.format("HH:mm");
  //       that.duration = moment(that.end).diff(that.start, "minutes");
  //       that.durHours = parseInt(that.duration / 60, 10);
  //       that.durMinutes = parseInt(that.duration % 60, 10);
  //       that.startTime = that.start.format(hma);
  //       that.endTime = that.end.format(hma);
  //       that.day = mode === "publish" ? that.start.format(dMD) : null;
  //       that.schedDay =
  //         mode === "publish"
  //           ? that.start.format("YYYY-MM-DD")
  //           : DAYS_OF_WEEK[res.extendedProps.dow];
  //       that.dayPretty = res.extendedProps && res.extendedProps.groupTitle;
  //       that.dow = mode === "publish" ? null : res.extendedProps.dow;
  //       that.title = schedule.title;
  //       that.employeeId = schedule.id
  //         ? res && res.extendedProps.employeeId
  //         : schedule.extendedProps.employeeId;
  //       that.published =
  //         schedule.extendedProps && schedule.extendedProps.published;
  //       that.editable = mode === "edit";
  //       that.hasChanged = hasChanged();
  //       that.schedType =
  //         mode === "edit"
  //           ? "Draft"
  //           : mode === "published"
  //           ? "Schedule"
  //           : "Template";
  //       that.conflicts = schedule.extendedProps.conflicts;
  //       that.temp = schedule.extendedProps.temp;
  //     };
  //     const onError = function (data) {
  //       if (that.revertFn) {
  //         that.revertFn();
  //       }
  //       alert.toast(data.message || "Oops! Error Occured!", "error", 3000);
  //       // $scope.alert.alert(data.message || "An error occured. Draft not saved.");
  //     };
  //     const deleteLocal = function () {
  //       if (schedule) {
  //         schedule.remove();
  //       }
  //       const resEvent = find(allSchedules, {
  //           id: getEventSourceIdForEvent(that),
  //         });
  //         let d;
  //       if (resEvent) {
  //         remove(resEvent.events, (item) => (
  //             (item.id && item.id === that.id) ||
  //             (item.reference && that.reference === item.reference)
  //           ));

  //         if (mode === "edit") {
  //           d = filter(allSchedules, (item) => !item.published);
  //           // $scope.hasDraft = !!d.length;
  //         }
  //         each(
  //           filter(calendarMethod("getEvents"), (ev) => {
  //             const ref =
  //               ev.reference ||
  //               (ev.extendedProps && ev.extendedProps.reference);
  //             return (
  //               (that.id && ev.id === that.id) ||
  //               (that.reference && ref === that.reference)
  //             );
  //           }),
  //           (ev) => {
  //             ev.remove();
  //           }
  //         );
  //         updateEventSources();
  //       }
  //     };

  //   if (res && !schedule.resource && !(schedule && schedule.getResources)) {
  //     res.extendedProps = {
  //       m: res.m,
  //       groupTitle: res.groupTitle,
  //       employeeId: res.employeeId,
  //       dow: res.dow,
  //     };
  //   }
  //   extend(that, {
  //     remove () {
  //       if (that.id) {
  //         return deleteThisEvent(
  //           that.id,
  //           (data) => {
  //             if (!(data && data.status)) {
  //               alert.toast("Oops, Failed to Delete!", "error", 3000);
  //               return onError(data);
  //             }
  //             alert.toast("Schedule Deleted!", "success", 2000);
  //             deleteLocal();
  //           },
  //           onError
  //         );
  //       }
  //       deleteLocal();
  //     },
  //     update (revertFn) {
  //       that.revertFn = revertFn || nth;
  //       update();
  //       if (hasChanged()) {
  //         const evObj = getEventForUpdate(that);
  //         return updateThisEvent(
  //           that.id,
  //           evObj,
  //           (data) => {
  //             if (!(data && data.status)) {
  //               alert("Oops, Update Failed!", "error", 3000);
  //               return onError(data);
  //             }
  //             alert(`${that.schedType  } Updated!`, "success", 2000);
  //             evObj.id = data.data.id;
  //             evObj.published = that.published;
  //             updateEventData(evObj);
  //           },
  //           onError
  //         );
  //       }
  //       return revertFn && revertFn();
  //     },
  //     changeStartTime (fall) {
  //       if (fall) {
  //         that.start.subtract(moment.duration(calendarSettings.snap));
  //       } else {
  //         const newStart = moment(that.start).add(
  //           moment.duration(calendarSettings.snap)
  //         );
  //         if (newStart.isBefore(that.end)) {
  //           that.start = newStart;
  //         }
  //       }
  //       update();
  //     },
  //     changeEndTime (fall) {
  //       if (fall) {
  //         const newEnd = moment(that.end).subtract(
  //           moment.duration(calendarSettings.snap)
  //         );
  //         if (newEnd.isAfter(that.start)) {
  //           that.end = newEnd;
  //         }
  //       } else {
  //         that.end.add(moment.duration(calendarSettings.snap));
  //       }
  //       update();
  //     },
  //     save (revertFn) {
  //       if (that.id) {
  //         return that.update(evObj);
  //       }
  //       that.revertFn = revertFn || nth;
  //       update();
  //       if (hasChanged() && !that.id && that.startHm && that.endHm) {
  //         var evObj = getEventForUpdate(that);
  //         if (evObj) {
  //           return updateThisEvent(
  //             that.id,
  //             evObj,
  //             (data) => {
  //               if (data && data.status) {
  //                 alert.toast(`${that.schedType  } Saved!`, "success", 2000);
  //                 merge(evObj, data.data);
  //                 removeTemporaryEvent();
  //                 return addEventToDrafts(evObj);
  //               }
  //               alert.toast("Oops, Save Failed!", "error", 3000);
  //               onError(data);
  //             },
  //             onError
  //           );
  //         }
  //       }
  //       return revertFn && revertFn();
  //     },
  //   });

  //   each(schedule, (v, k) => {
  //     if (!isUndefined(schedule[k])) {
  //       that[k] = schedule[k];
  //     }
  //   });
  //   that.id = schedule.id;
  //   that._ev = schedule;
  //   update();
  //   return that;
  // };

  // const publishedEvents = showPublished();
  const resources = getResources();

  const deleteEvent = async (eventId, businessId, skipRefresh = false) => {
    const bid = businessId || selectedBusiness.id;

    if (!eventId) return { status: false };
    const response = await callApi(
      `/protected/business/${bid}/schedule/delete/${eventId}/${
        mode === "template" ? "static" : "dynamic"
      }`,
      "POST"
    );
    if (response?.status) {
      if (mode === "template") {
        setModifiedBusinessIds(prev => new Set([...prev, bid]));
      }
      if (mode === "edit") setHasUnsavedDraft(true);
      if (!skipRefresh) getDynamicSchedules();
      return { status: true };
    }
    toast.error("Failed to delete schedule");
    return { status: false };
  };

  const checkAlternateDaysOverlap = (start1, start2) => {
    if (!start1 || !start2) return false;
    const a = moment(moment(start1).format("YYYY-MM-DD"));
    const b = moment(moment(start2).format("YYYY-MM-DD"));
    const diff = a.diff(b, "days", false);
    if (diff % 7 === 0 && (diff / 7) % 2 === 0) {
      return true;
    }
    return false;
  };

  const checkOverlap = (eventData, update) => {
    const eligibleEvents = publishedEvents
      .find((el) => el?.employeeId === eventData?.employee_id)
      ?.events?.filter((el) =>
        mode === "template"
          ? el?.extendedProps?.dow === eventData?.dow
          : el?.extendedProps?.day === eventData?.day
      )
      ?.map((el) => ({
        id: el?.id,
        start_time: el?.extendedProps?.startHm,
        end_time: el?.extendedProps?.endHm,
        detail: el?.extendedProps?.detail,
      }));
    if (!eligibleEvents?.length) return false;

    return !!eligibleEvents.find(
      (el) =>
        (!update || el.id !== eventData.id) &&
        el.end_time > eventData.start_time &&
        eventData.end_time > el.start_time &&
        (!el.detail?.alternate ||
          !eventData?.detail?.alternate ||
          checkAlternateDaysOverlap(
            eventData?.detail?.start,
            el?.detail?.start
          ))
    );
  };

  const addNewEvent = async (event) => {
    const businessId = event.extendedProps?.businessId || selectedBusiness.id;

    const timeFormat = "HH:mm";
    const addEventApi = `/protected/business/${businessId}/schedule/add`;
    const updateEventApi = `/protected/business/${businessId}/schedule/update/${event.id}/static`;
    const updateEventApiDynamic = `/protected/business/${businessId}/schedule/update/${event.id}/dynamic`;

    const maxTime = moment(calendarSettings?.storeHours?.end, "HH:mm");
    const minTime = moment(calendarSettings?.storeHours?.start, "HH:mm");

    const startTime = moment(event.start);
    const endTime = moment(event.end);

    if (event.setStart && event.setEnd) {
      // Adjust start time if it's before the min time
      if (startTime.isBefore(minTime)) {
        event.setStart(minTime.toDate());
      }
      // Adjust end time if it's after the max time
      if (endTime.isAfter(maxTime)) {
        event.setEnd(maxTime.toDate());
      }
    }

    const eventData = {
      start_time:
        event.start && moment(event.start).isValid()
          ? moment(event.start).format(timeFormat)
          : "09:00",
      end_time:
        event.end && moment(event.end).isValid()
          ? moment(event.end).format(timeFormat)
          : moment("09:00", timeFormat).add(8, "hours").format(timeFormat),
      day: event.extendedProps?.groupId || event.day,
      dow:
        event.extendedProps?.dow === 0
          ? 0
          : event.extendedProps?.dow || event.dow,
      employee_id: event.employeeId || event.extendedProps?.employeeId,
      auto: 0,
      detail: event.detail,
    };
    if (
      !moment(eventData.start_time, timeFormat).isValid() ||
      !moment(eventData.end_time, timeFormat).isValid()
    )
      return toast.error("Invalid Date");
    if (checkOverlap({ ...eventData, id: event.id }, event.update)) {
      toast.error("Cannot Set Overlapping Schedules");
      return { status: false };
    }
    if (mode === "template") {
      const response = await callApi(
        event.update ? updateEventApi : addEventApi,
        "POST",
        {
          start_time: `${eventData.start_time}`,
          end_time: `${eventData.end_time}`,
          day: null,
          dow: eventData.dow,
          employee_id: eventData.employee_id,
          auto: 0,
          template_id: selectedTemplate,
          detail: event.detail,
        }
      );
      // if (response.status) {
      //   // skip draft flag is used for direct edits from grid view in publish mode
      //   if (!event.skipDraft) setHasUnsavedDraft(true);
      //   await getDynamicSchedules();
      // }
      if (response.status) {
        if (!event.skipDraft) {
          setHasUnsavedDraft(true);
          await getDynamicSchedules();
        }
        setModifiedBusinessIds(prev => new Set([...prev, businessId]));
      }
      else toast.error("Failed to update data");
      return response;
    }

    const isDirect = event.skipDraft && mode === "publish" && !event.update;
    const response = await callApi(
      event.update ? updateEventApiDynamic : addEventApi,
      "POST",
      {
        start_time: `${eventData.start_time}`,
        end_time: `${eventData.end_time}`,
        day: eventData.day,
        dow: null,
        employee_id: eventData.employee_id,
        auto: 0,
        ...(isDirect && { direct: true }),
      }
    );
    if (response.status) {
      if (!event.skipDraft) {
        setHasUnsavedDraft(true);
        await getDynamicSchedules();
      }
    }
    return { ...response, direct: isDirect };
  };

  const getEffectiveTemplate = (templates, day) => {
    if (!templates || !templates.length) return 0;
    const dateToday = day || moment().format("YYYY-MM-DD");

    // eslint-disable-next-line no-restricted-syntax
    for (const el of templates) {
      if (el.start) {
        const startDate = moment(el.start);
        const todayDate = moment(dateToday);
        if (startDate.diff(todayDate, "days") <= 0) {
          if (!el.end) return el.id;

          const endDate = moment(el.end);
          if (endDate.diff(todayDate, "days") >= 0) {
            return el.id;
          }
        }
      }
    }
    return 0;
  };

  const checkValidity = (day, detail, templateId) => {
    //  handle template versions
    const effectiveTemplateId = calendarSettings?.multipleTemplate
      ? getEffectiveTemplate(calendarSettings?.templates, day)
      : 0;
    if (templateId !== effectiveTemplateId) return false;

    if (!detail) return true;

    //  handle exception
    if (detail.except && detail.except.length) {
      const date = moment(day).date();
      const quo = Math.ceil(date / 7);
      if (detail.except.includes(quo)) return false;
      if (detail.except.includes(5)) {
        const lastDate = moment(day).endOf("month").date();
        if (lastDate - date < 7) return false;
      }
    }

    //  handle Alternate Weeks
    if (detail.alternate === 1 && detail.start) {
      const date1 = moment(day);
      const date2 = moment(detail.start);
      const diff = date2.diff(date1, "days");
      if (diff > 0 || diff % 2 !== 0) return false;
    }

    return true;
  };

  // convert dow to day

  const dowToDay = (dayOfWeek, week) => {
    const date = moment();
    const day = date.day();
    if (dayOfWeek >= day) {
      return date.add(dayOfWeek - day + 7 * week, "day").format("YYYY-MM-DD");
    } 
      return date.add(7 + dayOfWeek - day + 7 * week, "day").format("YYYY-MM-DD");
    
  };

  const publishSchedules = async (noOfWeeks = 4, remainder, businessId = null) => {
    const bid = businessId || id;
    
    if (!allTemplateData) {
      toast.error("Couldn't Publish Schedules");
      return null;
    }

    // Filter template data to only this business's employees
    const businessEmployeeIds = businessId
      ? employeeData?.filter(emp => emp.business_id === businessId).map(emp => emp.id)
      : null;

    const temp = businessEmployeeIds
      ? allTemplateData.filter(el => businessEmployeeIds.includes(el.employee_id))
      : allTemplateData;

    let data = [];
    for (let i = 0; i < noOfWeeks; i += 1) {
      data.push(
        temp
          .filter((el) =>
            checkValidity(dowToDay(el.dow, i), el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, i),
          }))
      );
    }
    if (remainder) {
      const dayOfWeek = moment().day();
      const eligible = [];
      for (let i = 0; i < remainder; i += 1) {
        eligible.push((dayOfWeek + i) % 7);
      }
      data.push(
        temp
          .filter((el) => eligible?.includes(el.dow))
          .filter((el) =>
            checkValidity(
              dowToDay(el.dow, noOfWeeks),
              el.detail,
              el.template_id
            )
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, noOfWeeks),
          }))
      );
    }
    data = flatten(data);
    const res = await callApi(
      `/protected/business/${bid}/schedule/clear/republish`,
      "POST",
      data
    );
    if (res && res.status) {
      toast.success(
        `Schedule published for the next ${
          remainder
            ? `${noOfWeeks * 7 + remainder} days`
            : `${noOfWeeks === 1 ? "week" : `${noOfWeeks} weeks`}`
        }`
      );
      await getDynamicSchedules();
      setMode("publish");
      return res.data;
    } 
    toast.error(res?.message || "Failed to publish schedules");
    return null;
  };

  const clearPublishedForPeriod = async (businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    const response = await callApi(
      `/protected/business/${bid}/schedule/clear/periodschedules`,
      "POST",
      { start: period[0], end: period[1] },
    );
    if (response?.status) {
      await getPastSchedule({ 
        start: period[0], 
        end: period[1],
        businessIds: [bid],
      });
      toast.success("Schedules cleared successfully");
    } else toast.error(response?.message || "Failed to clear schedules");
  };

  const publishSchedulesForBusinesses = async (
    businessIds,
    noOfWeeks = 4,
    remainder,
    locationOptions
  ) => {
    const res = await callApi(
        `/protected/business/${id}/network/schedule/republishforall`,
        "POST",
        {
            businessIds,
            noOfWeeks,
            remainder,
        }
    );
    if (!res?.status) {
        toast.error(res?.message || "Failed to publish schedules");
        return null;
    }
    const getName = (bid) =>
        locationOptions?.find(b => b.id === bid)?.name || `Business ${bid}`;
    const succeeded = (res.data?.succeeded || []).map(getName);
    const failed    = (res.data?.failed    || []).map(f => getName(f.businessId));
    if (succeeded.length > 0 && failed.length === 0) {
        toast.success(`Schedule published successfully for ${succeeded.length} location(s)`);
    } else if (succeeded.length > 0 && failed.length > 0) {
        toast.success(`Published for: ${succeeded.join(", ")}`);
        toast.error(`Failed for: ${failed.join(", ")}`);
    } else {
        toast.error(`Failed to publish for: ${failed.join(", ")}`);
        return null;
    }

    await getDynamicSchedules();
    setMode("publish");
    return succeeded;
  };

  const clearDraft = async (data, skipRefresh = false) => {
    const clearDraftApi = `/protected/business/${selectedBusiness.id}/schedule/clear/drafts?clearAll=1`;
    let drafts = flatten(map(allData, "dynamicschedules"));
    if (!data?.all) {
      drafts = drafts.filter((el) => data?.includes(el.employee_id));
    }
    drafts = drafts.filter((el) => !el.published).map((el) => `${el.id}`);
    await callApi(clearDraftApi, "POST", drafts);
    if (!skipRefresh) {
      await getDynamicSchedules();
    }
    setDraftAvailable(false);
    if (!skipRefresh) {
      toast.success("Drafts cleared successfully");
    }
  };

  const clearDraftAndCopyFromTemplate = async ( businessId = null ) => {
    const bid = businessId || selectedBusiness.id;

    if (!allTemplateData) {
      toast.error("Template Not Found");
      return;
    }

    const businessEmployeeIds = businessId
      ? employeeData?.filter(emp => emp.business_id === businessId).map(emp => emp.id)
      : null;

    const temp = businessEmployeeIds
      ? allTemplateData.filter(el => businessEmployeeIds.includes(el.employee_id))
      : allTemplateData;

    let batches = [];
    const effectiveDate = period[0];
    const offset = moment(effectiveDate).day();
    for (let i = 0; i < calendarSettings.viewDays; i += 1) {
      const newDay = moment(effectiveDate).add(i, "days").format("YYYY-MM-DD");
      const newDow = (offset + i) % 7;
      batches.push(
        temp
          .filter(
            (el) =>
              el.dow === newDow &&
              checkValidity(newDay, el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: newDay,
          }))
      );
    }

    batches = flatten(batches);

    setFetchLoading(true);

    const addBatchApi = `/protected/business/${bid}/schedule/addbatch`;

    const clearDraftApi = `/protected/business/${bid}/schedule/clear/drafts`;

    const allDrafts = flatten(map(allData, "dynamicschedules"))
      .filter((el) => !el.published);
  
    const drafts = businessEmployeeIds
      ? allDrafts.filter(el => businessEmployeeIds.includes(el.employee_id)).map(el => `${el.id}`)
      : allDrafts.map(el => `${el.id}`);

    await callApi(clearDraftApi, "POST", drafts);

    setHasUnsavedDraft(false);
    const res = await callApi(addBatchApi, "POST", batches);

    await getDynamicSchedules();
    setFetchLoading(false);
    if (res?.status) toast.success("Drafts copied from template");
    else toast.error(res?.message || "Failed to copy from template");
  };

  const replaceWithTemplateForPeriod = async (businessId = null) => {
    const bid = businessId || selectedBusiness.id;

    if (!allTemplateData) {
      toast.error("Template Not Found");
      return;
    }

    const businessEmployeeIds = businessId
      ? employeeData?.filter(emp => emp.business_id === businessId).map(emp => emp.id)
      : null;

    const temp = businessEmployeeIds
      ? allTemplateData.filter(el => businessEmployeeIds.includes(el.employee_id))
      : allTemplateData;

    // Build template batches for current period
    let batches = [];
    const periodDays = moment(period[1]).diff(moment(period[0]), "days") + 1;
    const offset = moment(period[0]).day();

    for (let i = 0; i < periodDays; i += 1) {
      const newDay = moment(period[0]).add(i, "days").format("YYYY-MM-DD");
      const newDow = (offset + i) % 7;
      batches.push(
        temp
          .filter(el => el.dow === newDow && checkValidity(newDay, el.detail, el.template_id))
          .map(el => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: newDay,
          }))
      );
    }

    batches = flatten(batches);

    if (!batches.length) {
      toast.error("No template schedules found for this period.");
      return;
    }

    setFetchLoading(true);

    // Clear existing drafts in the period before adding new ones
    const existingDrafts = flatten(map(allData, "dynamicschedules"))
      .filter(el => !el.published)
      .filter(el => el.day >= period[0] && el.day <= period[1])
      .filter(el => businessEmployeeIds ? businessEmployeeIds.includes(el.employee_id) : true)
      .map(el => String(el.id));

    if (existingDrafts.length > 0) {
      await callApi(
        `/protected/business/${bid}/schedule/clear/drafts`,
        "POST",
        existingDrafts
      );
    }

    // Create drafts from template
    const addRes = await callApi(
      `/protected/business/${bid}/schedule/addbatch`,
      "POST",
      batches
    );

    if (!addRes?.status) {
      toast.error(addRes?.message || "Failed to apply template");
      setFetchLoading(false);
      return;
    }

    // Get fresh draft IDs scoped to period
    const freshData = await getDynamicSchedules(null, [bid]);
    const draftIds = flatten(map(freshData?.data, "dynamicschedules"))
      .filter(el => !el.published)
      .filter(el => el.day >= period[0] && el.day <= period[1])
      .filter(el => businessEmployeeIds ? businessEmployeeIds.includes(el.employee_id) : true)
      .map(el => String(el.id));

    if (!draftIds.length) {
      toast.error("No drafts found to publish. Template may be empty for this period.");
      setFetchLoading(false);
      return;
    }

    // Publish scoped to current period
    const publishRes = await callApi(
      `/protected/business/${bid}/schedule/publish`,
      "POST",
      { drafts: draftIds, start: period[0], end: period[1] }
    );

    if (publishRes?.status) {
      await getPastSchedule({
        start: period[0],
        end: period[1],
        businessIds: [bid],
      });
      toast.success("Template applied successfully");
    } else {
      toast.error(publishRes?.message || "Failed to publish template schedules");
    }

    setFetchLoading(false);
  };

  const clearTemplate = async (data, businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    const clearTemplateApi = `/protected/business/${bid}/schedule/clear/templates`;
    const response = await callApi(clearTemplateApi, "POST", {
      template_id: selectedTemplate,
      eid : (data?.all ? undefined : data)
    });
    if (response?.status) {
      getDynamicSchedules();
      toast.success("Master Template cleared successfully");
    } else toast.error("Failed to clear template");
  };

  const clearTemplateForBusinesses = async (businessIds, locationOptions) => {
    const res = await callApi(
      `/protected/business/${id}/network/schedule/clearall/templates`,
      "POST",
      {
        businessIds,
        template_id: selectedTemplate,
      },
    );

    if (!res?.status) {
      toast.error(res?.message || "Failed to clear templates");
      return null;
    }

    const getName = (bid) =>
      locationOptions?.find((b) => b.id === bid)?.name || `Business ${bid}`;

    const succeeded = (res.data?.succeeded || []).map(getName);
    const failed = (res.data?.failed || []).map((f) => getName(f.businessId));

    if (succeeded.length > 0 && failed.length === 0) {
      toast.success(
        `Template cleared successfully for ${succeeded.length} location(s)`,
      );
    } else if (succeeded.length > 0 && failed.length > 0) {
      toast.success(`Cleared for: ${succeeded.join(", ")}`);
      toast.error(`Failed for: ${failed.join(", ")}`);
    } else {
      toast.error(`Failed to clear for: ${failed.join(", ")}`);
      return null;
    }

    await getDynamicSchedules();
    return succeeded;
  };

  const addBatch = async (startDate, skipRefresh = false, businessId = null) => {
    let data;
    const bid = businessId || selectedBusiness.id;

    if (
      startDate ||
      moment(selectedDate).format("YYYY-MM-DD") !==
        moment().format("YYYY-MM-DD")
    ) {
      const resp = await getDynamicSchedules(startDate);
      if (!resp?.status) {
        return toast.error("Unknown Error occurred!");
      }
      data = resp.data || [];
    } else {
      data = allData;
    }
    setFetchLoading(true);

    const allDynamicSchedules = flatten(map(data, "dynamicschedules"));
    const filteredSchedules = locationsLoaded
      ? allDynamicSchedules.filter(el => {
          const businessEmployeeIds = employeeData
            ?.filter(emp => emp.business_id === bid)
            .map(emp => emp.id);
          return businessEmployeeIds.includes(el.employee_id);
        })
      : allDynamicSchedules;

    const addBatchApi = `/protected/business/${bid}/schedule/addbatch`;
    const batches = filteredSchedules
      .filter((el) => el.published)
      .map((el) => ({
        start_time: el.start_time,
        end_time: el.end_time,
        day: el.day,
        dow: null,
        auto: 0,
        employee_id: el.employee_id,
      }));
    const clearDraftApi = `/protected/business/${bid}/schedule/clear/drafts`;
    const drafts = filteredSchedules
      .filter((el) => !el.published)
      .map((el) => `${el.id}`);
    await callApi(clearDraftApi, "POST", drafts);
    setHasUnsavedDraft(false);
    const response = await callApi(addBatchApi, "POST", batches);
    // if(response.status) toast.success("Drafts added from Published Schedule")

    if (response?.status && !skipRefresh) await getDynamicSchedules(startDate);
    setFetchLoading(false);
    return { status: true };
  };

  const publishDraft = async () => {
    const publishEventApi = `/protected/business/${selectedBusiness.id}/schedule/publish`;
    const drafts = flatten(map(allData, "dynamicschedules"))
      .filter((el) => !el.published)
      .map((el) => `${el.id}`);
    await callApi(publishEventApi, "POST", {
      drafts,
      start: moment(selectedDate).format("YYYY-MM-DD"),
      end: moment(selectedDate)
        .add(calendarSettings.viewDays - 1, "days")
        .format("YYYY-MM-DD"),
    });
    await getDynamicSchedules();
    setMode("publish");
    toast.success("Drafts published to schedules");
  };

  const deleteStaffSchedule = async (employeeId, start, businessId = null ) => {
    const bid = businessId || selectedBusiness.id;
    const response = await callApi(
      `/protected/business/${bid}/schedule/clearall/${employeeId}`,
      "POST",
      {
        start: start || `${moment().format("YYYY-MM-DD")}`,
        published: mode === "publish",
      }
    );
    if (response.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      await getDynamicSchedules();
    }
  };

  const republishStaffSchedule = async (
    employeeId,
    noOfWeeks = 4,
    remainder,
    allTemplateDataWorkflow,
    businessId = null,
  ) => {
    const bid = businessId || selectedBusiness?.id;
    if (!allTemplateData) {
      toast.error("Couldn't Publish Schedules");
      return;
    }

    const temp = (allTemplateDataWorkflow && allTemplateDataWorkflow.length)
      ? allTemplateDataWorkflow.filter((el) => employeeId === el.employee_id)
      : allTemplateData?.filter((el) => employeeId === el.employee_id);
    
    // eslint-disable-next-line no-console
    console.log("In republish Function ", employeeId, noOfWeeks, remainder,{allTemplateData,allTemplateDataWorkflow,temp});

    let data = [];
    for (let i = 0; i < noOfWeeks; i += 1) {
      data.push(
        temp
          .filter((el) =>
            checkValidity(dowToDay(el.dow, i), el.detail, el.template_id)
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, i),
          }))
      );
    }
    if (remainder) {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const eligible = [];
      for (let i = 0; i < remainder; i += 1) {
        eligible.push((dayOfWeek + i) % 7);
      }
      data.push(
        temp
          .filter((el) => eligible?.includes(el.dow))
          .filter((el) =>
            checkValidity(
              dowToDay(el.dow, noOfWeeks),
              el.detail,
              el.template_id
            )
          )
          .map((el) => ({
            employee_id: el.employee_id,
            start_time: el.start_time,
            end_time: el.end_time,
            dow: null,
            auto: 0,
            day: dowToDay(el.dow, noOfWeeks),
          }))
      );
    }
    data = flatten(data);
    const response = await callApi(
      `/protected/business/${bid}/schedule/clear/republishfor/${employeeId}`,
      "POST",
      data
    );
    if (response.status) {
      toast.success(
        `Staff Schedule published for the next ${
          remainder
            ? `${noOfWeeks * 7 + remainder} days`
            : `${noOfWeeks === 1 ? "week" : `${noOfWeeks} weeks`}`
        }`
      );
      await getDynamicSchedules();
    }
    else {
      toast.error(response?.message || "Failed to publish staff schedule");
    }
  };

  const assignStaffColor = async (employeeId, color, businessId = null ) => {
    const bid = businessId || selectedBusiness?.id;
    const attributes = find(defaultSchedules, { employeeId })?.attributes;
    const response = await callApi(
      `/protected/business/${bid}/staff/${employeeId}/property/update`,
      "POST",
      {
        id: employeeId,
        key: "attributes",
        value: {
          ...attributes,
          schedulerColor: color,
        },
      }
    );
    if (response.status) {
      toast.success("Staff Color assigned");
      updateEmployeeData({ id: employeeId, attributes: { ...attributes, schedulerColor: color } });
    } else {
      toast.error(response?.message || "Failed to assign staff color");
    }
  };

  const copyFromPreviousDay = async (targetDay, businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    const isTemplate = mode === "template";
    const sourceDay = isTemplate
      ? targetDay - 1
      : moment(targetDay).subtract(1, "day").format(YMD_FORMAT);
    const response = await callApi(
      `/protected/business/${bid}/schedule/copy`,
      "POST",
      {
        mode: "all",
        sourceDay,
        targetDays: [targetDay],
        published: mode !== "edit",
        ...(isTemplate && { templateId: selectedTemplate }),
      }
    );
    if (response?.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      toast.success(`Copied schedule from ${isTemplate ? dow[sourceDay] : sourceDay}`);
      if (mode === "publish") {
        await getPastSchedule({ start: period[0], end: period[1], businessIds: [bid] });
      } else {
        await getDynamicSchedules();
      }
    } else {
      toast.error(response?.message || "Failed to copy schedule");
    }
    return response;
  };

  const copyToDays = async (sourceDay, targetDays, employeeIds, businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    const isTemplate = mode === "template";
    const response = await callApi(
      `/protected/business/${bid}/schedule/copy`,
      "POST",
      {
        mode: "staff",
        sourceDay,
        targetDays,
        employeeIds,
        published: mode !== "edit",
        ...(isTemplate && { templateId: selectedTemplate }),
      }
    );
    if (response?.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      toast.success("Schedule copied to selected days");
      if (mode === "publish") {
        await getPastSchedule({ start: period[0], end: period[1], businessIds: [bid] });
      } else {
        await getDynamicSchedules();
      }
    } else {
      toast.error(response?.message || "Failed to copy schedule");
    }
    return response;
  };

  const copyStaffSchedule = async (sourceEmployeeId, targetEmployeeIds, days, businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    const isTemplate = mode === "template";
    const response = await callApi(
      `/protected/business/${bid}/schedule/copy`,
      "POST",
      {
        mode: "employee",
        sourceEmployeeId,
        targetEmployeeIds,
        days,
        published: mode !== "edit",
        ...(isTemplate && { templateId: selectedTemplate }),
      }
    );
    if (response?.status) {
      if (mode === "edit") setHasUnsavedDraft(true);
      toast.success("Schedule copied to selected staff");
      if (mode === "publish") {
        await getPastSchedule({ start: period[0], end: period[1], businessIds: [bid] });
      } else {
        await getDynamicSchedules();
      }
    } else {
      toast.error(response?.message || "Failed to copy schedule");
    }
    return response;
  };

  const resetLocationTracking = () => {
    setLoadedBusinessIds(new Set());
    setSchedulesByBusiness({});
    setPeriodScheduleByBusiness({});
    setHolidaysByBusiness({});
    // selectedBusinessIds intentionally preserved
  };

  const fetchNetworkHolidays = async (businessIds) => {
    if (!businessIds?.length) return;
    const res = await callApi(
      `/protected/business/${id}/network/holidays/fetch`,
      "GET",
      null,
      null,
      { businessIds }
    );
    if (res?.status) {
      setHolidaysByBusiness(prev => ({ ...prev, ...res.data }));
    }
  };

  const handleLocationSelection = async (newSelection) => {
    if (!newSelection) return;

    // selectedBusiness.id (id) always in selectedBusinessIds.
    if (newSelection.length === 0) {
      setSelectedBusinessIds([id]);
      return;
    }

    const enforced = newSelection.includes(id)
      ? newSelection
      : [id, ...newSelection];

    const added = enforced.filter(bid => !loadedBusinessIds.has(bid));

    if (added.length > 0) {
      const nonPrimaryAdded = added.filter(bid => bid !== id);
      if (nonPrimaryAdded.length > 0) {
        fetchNetworkHolidays(nonPrimaryAdded);
      }
      // Employees, just the newly added locations
      await getEmployeeData(false, true, added);
      await getDynamicSchedules(null, enforced);

      if (mode === "publish") {
        await getPastSchedule({ 
          start: period[0], 
          end: period[1], 
          businessIds: enforced,
        });
      }

      setLoadedBusinessIds(prev => new Set([...prev, ...added]));
    }

    setSelectedBusinessIds(enforced);
  };

  const getHistory = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/activity/schedule`,
      "GET"
    );
    return response;
  };

  const updateHolidays = async (holiday) => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/update`,
      "POST",
      { holidays: holiday }
    );
    return response;
  };

  const fetchSettings = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/fetch`,
      "GET"
    );
    if (response && response.data?.settings) {
      setCalendarSettings({
        ...defaultCalendarSettings,
        ...response.data.settings,
      });
      updateCurrentBusiness({
        ...selectedBusiness,
        info: {
          ...selectedBusiness.info,
          calendarSettings: response.data.settings,
        },
      });
    } else {
      setCalendarSettings({ ...defaultCalendarSettings });
    }
    if (response && response.data?.holidays) {
      setHolidays(response.data.holidays);
      setHolidaysByBusiness(prev => ({
        ...prev,
        [id]: response.data.holidays,
      }));
    }
  };

  const updateSettings = async (settings) => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/settings`,
      "POST",
      settings
    );
    await fetchSettings();
    return response;
  };

  const updateTemplates = async (templates) => {
    const settings = { ...calendarSettings };
    settings.templates = templates;
    const response = await updateSettings(settings);
    return response;
  };

  const addTemplate = async (name, start, end) => {
    const templates = calendarSettings?.templates || [];
    const newId = (maxBy(templates, (x) => x.id)?.id || 0) + 1;
    await updateTemplates([...templates, { id: newId, name, start, end }]);
    setSelectedTemplate(newId);
  };

  const editTemplate = async (tempID, name, start, end) => {
    const templates = calendarSettings?.templates || [];
    if (
      (tempID === 0 || tempID === 1) &&
      !templates.find((x) => x.id === tempID)
    )
      await addTemplate(name, start);
    else
      await updateTemplates(
        templates.map((x) => {
          if (x.id !== tempID) return x;
          return { id: x.id, name, start, end };
        })
      );
  };

  const handleFutureTemplateSync = async (templateId) => {
    //  clear current template
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/clear/templates`,
      "POST"
    );

    // convert future template to current template
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/update/templates`,
      "POST",
      {
        from: templateId,
        to: 0,
      }
    );

    // remove multiple template from settings
    await callApi(
      `/protected/business/${selectedBusiness.id}/schedule/settings`,
      "POST",
      { ...calendarSettings, multipleTemplate: false, templates: undefined }
    );

    await fetchSettings();
    await getDynamicSchedules();
  };

  const fetchHolidays = async () => {
    const response = await callApi(
      `/protected/business/${selectedBusiness.id}/holidays/fetch`,
      "GET"
    );
    if (response?.data?.holidays) {
      setHolidays(response.data.holidays);
      setHolidaysByBusiness(prev => ({
        ...prev,
        [id]: response.data.holidays,
      }));
    }
    return response;
  };

  const copyPublishedToTemplate = async (startDate, businessId = null) => {
    const bid = businessId || selectedBusiness.id;
    setFetchLoading(true);

    const res = await callApi(
        `/protected/business/${id}/network/schedule/copypublishedtotemplates`,
        "POST",
        {
            businessIds: [bid],
            startDate,
            templateId: selectedTemplate,
        }
    );

    setFetchLoading(false);

    if (!res?.status) {
        toast.error(res?.message || "Failed to copy schedule to template");
        return { status: false };
    }

    const succeeded = res.data?.succeeded || [];
    const failed    = res.data?.failed   || [];

    if (succeeded.length > 0) {
        await getDynamicSchedules();
        toast.success("Published schedule copied to Master Template successfully!");
        return { status: true };
    }

    toast.error(failed?.[0]?.error || "Failed to copy schedule to template");
    return { status: false };
  };

  const copyPublishedToTemplateForBusinesses = async (
    businessIds,
    startDate,
    locationOptions,
  ) => {
    const res = await callApi(
      `/protected/business/${id}/network/schedule/copypublishedtotemplates`,
      "POST",
      {
        businessIds,
        startDate,
        templateId: selectedTemplate,
      },
    );

    if (!res?.status) {
      toast.error(res?.message || "Failed to copy schedules to template");
      return null;
    }

    const getName = (bid) =>
      locationOptions?.find((b) => b.id === bid)?.name || `Business ${bid}`;

    const succeeded = (res.data?.succeeded || []).map(getName);
    const failed = (res.data?.failed || []).map((f) => ({
      name: getName(f.businessId),
      error: f.error,
    }));

    if (succeeded.length > 0 && failed.length === 0) {
      toast.success(
        `Schedule copied to template for ${succeeded.length} location(s)`,
      );
    } else if (succeeded.length > 0 && failed.length > 0) {
      toast.success(`Copied for: ${succeeded.join(", ")}`);
      failed.forEach((f) => toast.error(`${f.name}: ${f.error}`));
    } else {
      failed.forEach((f) => toast.error(`${f.name}: ${f.error}`));
      return null;
    }

    await getDynamicSchedules();
    return succeeded;
  };

  const getArchivedSchedules = async (type = "published", options = {}) => {
    const { start, end, includeDrafts = false } = options;
  
    let queryString = `?type=${type}`;
  
    // Only add date filter if explicitly provided
    if (start && end) {
      queryString += `&start=${start}&end=${end}`;
    }
  
    if (includeDrafts) {
      queryString += `&include_drafts=true`;
    }
  
    const response = await callApiIO(
      `/protected/business/${selectedBusiness.id}/iorequest?dest=${encodeURIComponent(
        `business/:BID:/schedule/archived${queryString}`
      )}`,
      "GET",
      null,
      null,
      null
    );
    return response;
  };
  
  const restoreArchivedSchedule = async (schedules, isTemplate = false, clearAndRestore = false, skipRefresh = false) => {
    try {
      const scheduleList = Array.isArray(schedules) ? schedules : [schedules];
      
      const addBatchApi = `/protected/business/${selectedBusiness.id}/schedule/addbatch`;

      // Deduplicate input
      const seen = new Set();
      const uniqueScheduleList = [];
      
      // eslint-disable-next-line no-restricted-syntax
      for (const schedule of scheduleList) {
        const key = isTemplate
          ? `${schedule.employee_id}-${schedule.dow}-${schedule.start_time}-${schedule.end_time}`
          : `${schedule.employee_id}-${schedule.day}-${schedule.start_time}-${schedule.end_time}`;
        
        // eslint-disable-next-line no-continue
        if (seen.has(key)) continue;
        seen.add(key);
        uniqueScheduleList.push(schedule);
      }

      if (uniqueScheduleList.length === 0) {
        return { status: false, message: "No schedules to restore", restored: 0, skipped: 0 };
      }

      //  clear all existing drafts in the date range first
      if (clearAndRestore && !isTemplate) {
        const days = uniqueScheduleList.map(s => s.day).filter(d => d).sort();
        const startDate = days[0];
        const endDate = days[days.length - 1];

        // Fetch current data for the date range to find ALL drafts to delete
        const fetchResponse = await callApi(
          `/protected/business/${id}/schedule/get/dynamic`,
          "get",
          null,
          null,
          { start: startDate }
        );
        
        if (fetchResponse?.status) {
          // Find all drafts in the target date range (regardless of employee)
          // This clears the entire period so only restored schedules remain
          const draftsToDelete = flatten(map(fetchResponse.data, "dynamicschedules"))
            .filter(el => !el.published && !el.archived_at)
            .filter(el => el.day >= startDate && el.day <= endDate)
            .map(el => `${el.id}`);
          
          if (draftsToDelete.length > 0) {
            await callApi(
              `/protected/business/${selectedBusiness.id}/schedule/clear/drafts`,
              "POST",
              draftsToDelete
            );
          }
        }

        // Build schedules to add
        const validSchedules = uniqueScheduleList.map(schedule => ({
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          day: schedule.day,
          dow: null,
          employee_id: schedule.employee_id,
          auto: 0,
          detail: schedule.detail || null,
        }));

        // Add schedules
        const response = await callApi(addBatchApi, "POST", validSchedules);

        if (response?.status) {
          setHasUnsavedDraft(true);
        }

        return { 
          ...response, 
          restored: validSchedules.length,
          skipped: 0,
        };
      }

      // SKIP OVERLAPPING PATH - Check for overlaps
      const validSchedules = [];
      const skippedSchedules = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const schedule of uniqueScheduleList) {
        if (isTemplate) {
          // Template mode - check overlap
          const overlapData = {
            employee_id: schedule.employee_id,
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            dow: schedule.dow,
            day: null,
          };

          if (checkOverlap(overlapData, false)) {
            skippedSchedules.push({ ...schedule, reason: "Overlap" });
            // eslint-disable-next-line no-continue
            continue;
          }

          validSchedules.push({
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            day: null,
            dow: schedule.dow,
            employee_id: schedule.employee_id,
            auto: 0,
            template_id: schedule.template_id || selectedTemplate || 0,
            detail: schedule.detail || null,
          });
        } else {
          // Published mode - check overlap
          const overlapData = {
            employee_id: schedule.employee_id,
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            day: schedule.day,
            dow: null,
          };
          
          if (checkOverlap(overlapData, false)) {
            skippedSchedules.push({ ...schedule, reason: "Overlap" });
            // eslint-disable-next-line no-continue
            continue;
          }

          validSchedules.push({
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            day: schedule.day,
            dow: null,
            employee_id: schedule.employee_id,
            auto: 0,
            detail: schedule.detail || null,
          });
        }
      }

      if (validSchedules.length === 0) {
        return { 
          status: false, 
          message: "All schedules skipped (overlapping)",
          skipped: skippedSchedules.length,
          restored: 0,
        };
      }

      const response = await callApi(addBatchApi, "POST", validSchedules);

      if (response?.status) {
        if (!isTemplate) {
          setHasUnsavedDraft(true);
        }
        if (!skipRefresh) {
          await getDynamicSchedules();
        }
      }

      return { 
        ...response, 
        restored: validSchedules.length,
        skipped: skippedSchedules.length,
        skippedDetails: skippedSchedules,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error restoring schedules:", error);
      return { status: false, message: "Failed to restore schedules" };
    }
  };

  useEffect(async () => {
    if (firstRenderRef.current && !allData?.length) await getDynamicSchedules();
    else firstRenderRef.current = true;
  }, [selectedDate]);

  useEffect(() => {
    setPublishedEvents(showPublished());
  }, [allData, mode, employeeData, periodicSchedule]);

  // useEffect(() => {
  //     console.log('why this is loading for long time')
  //     // getEmployeeData()
  //     getDynamicSchedules();
  // },[])

  useEffect(async () => {
    // Skip addBatch if we just restored schedules (to prevent deleting restored drafts)
    if (skipAddBatchRef.current) {
      skipAddBatchRef.current = false;
      return;
    }
    
    if (mode === "edit") {
      await addBatch(selectedDate);
    }
  }, [selectedDate]);

  return {
    fetchLoading,
    mode,
    setMode,
    selectedDate,
    setSelectedDate,
    period,
    setPeriod,
    calendarSettings,
    defaultSchedules: defaultSchedulesWithHours,
    publishedEvents,
    resources,
    addNewEvent,
    getDynamicSchedules,
    getEmployeeData,
    employeeData: scopedEmployeeData,
    setRefetch,
    refetch,
    publishSchedules,
    deleteEvent,
    publishDraft,
    addBatch,
    getHistory,
    clearDraft,
    clearDraftAndCopyFromTemplate,
    clearTemplate,
    deleteStaffSchedule,
    republishStaffSchedule,
    assignStaffColor,
    copyFromPreviousDay,
    copyToDays,
    copyStaffSchedule,
    draftAvailable,
    updateHolidays,
    fetchHolidays,
    fetchSettings,
    updateSettings,
    updateTemplates,
    addTemplate,
    editTemplate,
    setNDays,
    holidays,
    selectedTemplate,
    setSelectedTemplate,
    getEffectiveTemplate,
    handleFutureTemplateSync,
    dynamicSchedules,
    staticSchedules,
    getPastSchedule,
    periodicSchedule,
    hasUnsavedDraft,
    setHasUnsavedDraft,
    updateEmployeeAppointmentVisibility,
    copyPublishedToTemplate,
    getArchivedSchedules,
    restoreArchivedSchedule,
    setSkipAddBatch,
    refreshSchedules,
    publishSchedulesForBusinesses,
    clearTemplateForBusinesses,
    copyPublishedToTemplateForBusinesses,
    modifiedBusinessIds,
    clearPublishedForPeriod,
    replaceWithTemplateForPeriod,
    locationsLoaded,
    selectedBusinessIds,
    handleLocationSelection,
    resetLocationTracking,
    holidaysByBusiness,
    fetchNetworkHolidays,
  };
};

export default useScheduleData;
